/**
 * Sticky Add to Cart Web Component for Love Knots
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.submitBtn = this.querySelector(`#StickyAtcSubmit-${this.sectionId}`);
        this.variantSelect = this.querySelector(`#StickyAtcVariantSelect-${this.sectionId}`);
        this.priceEl = this.querySelector(`#StickyAtcPrice-${this.sectionId}`);
        this.imageEl = this.querySelector(`#StickyAtcImage-${this.sectionId}`);
        this.qtyInput = this.querySelector('.sticky-atc__qty-input');
        this.qtyMinus = this.querySelector('.sticky-atc__qty-minus');
        this.qtyPlus = this.querySelector('.sticky-atc__qty-plus');
        this.couponBtn = this.querySelector('.sticky-atc__coupon-btn');
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.initEvents();
      }

      connectedCallback() {
        this.setupIntersectionObserver();
        this.subscribeToVariantChanges();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      initEvents() {
        // Quantity Plus
        if (this.qtyPlus && this.qtyInput) {
          this.qtyPlus.addEventListener('click', () => {
            const current = parseInt(this.qtyInput.value) || 1;
            this.qtyInput.value = current + 1;
          });
        }

        // Quantity Minus
        if (this.qtyMinus && this.qtyInput) {
          this.qtyMinus.addEventListener('click', () => {
            const current = parseInt(this.qtyInput.value) || 1;
            if (current > 1) {
              this.qtyInput.value = current - 1;
            }
          });
        }

        // Dropdown variant change from sticky bar
        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', (e) => {
            this.handleStickyVariantChange(e.target);
          });
        }

        // Submit Button Click (AJAX Add to Cart)
        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addToCart();
          });
        }

        // Coupon Copy Button Click
        if (this.couponBtn) {
          this.couponBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.copyCouponCode();
          });
        }
      }

      copyCouponCode() {
        const code = this.couponBtn.dataset.couponCode;
        if (!code) return;

        const copyTextEl = this.couponBtn.querySelector('.sticky-atc__copy-text');
        const originalText = copyTextEl ? copyTextEl.textContent : 'COPY';

        const onSuccess = () => {
          this.couponBtn.classList.add('is-copied');
          if (copyTextEl) copyTextEl.textContent = 'COPIED! ✓';

          setTimeout(() => {
            this.couponBtn.classList.remove('is-copied');
            if (copyTextEl) copyTextEl.textContent = originalText;
          }, 2500);
        };

        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(code).then(onSuccess).catch(() => {
            this.fallbackCopy(code, onSuccess);
          });
        } else {
          this.fallbackCopy(code, onSuccess);
        }
      }

      fallbackCopy(text, callback) {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          if (callback) callback();
        } catch (err) {
          console.error('Failed to copy', err);
        }
        textArea.remove();
      }

      setupIntersectionObserver() {
        const targetBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector(`#MainProduct-${this.sectionId} [type="submit"]`);

        if (!targetBtn) {
          // Fallback: Show on scroll past 400px
          window.addEventListener('scroll', () => {
            if (window.scrollY > 400) {
              this.classList.add('is-visible');
            } else {
              this.classList.remove('is-visible');
            }
          }, { passive: true });
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // When the main Add to Cart button is scrolled past the top of the viewport
              if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                this.classList.add('is-visible');
              } else {
                this.classList.remove('is-visible');
              }
            });
          },
          {
            rootMargin: '0px 0px 0px 0px',
            threshold: 0
          }
        );

        this.observer.observe(targetBtn);
      }

      subscribeToVariantChanges() {
        // Listen to Dawn's PubSub variant changes
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId !== this.sectionId) return;
            const variantId = event.data.variant ? event.data.variant.id : null;
            if (variantId && this.variantSelect) {
              this.variantSelect.value = variantId;
              this.updateDisplayForVariant(this.variantSelect.selectedOptions[0]);
            }
          });
        }

        // Direct DOM event fallback
        document.addEventListener('variant:change', (e) => {
          if (e.detail && e.detail.variant && this.variantSelect) {
            this.variantSelect.value = e.detail.variant.id;
            this.updateDisplayForVariant(this.variantSelect.selectedOptions[0]);
          }
        });
      }

      handleStickyVariantChange(selectElement) {
        const selectedOption = selectElement.selectedOptions[0];
        if (!selectedOption) return;

        this.updateDisplayForVariant(selectedOption);

        // Sync with the main form variant inputs
        const variantId = selectedOption.value;
        const hiddenVariantInput = this.querySelector('.sticky-atc__variant-id');
        if (hiddenVariantInput) {
          hiddenVariantInput.value = variantId;
        }

        const mainVariantSelect = document.querySelector(`#MainProduct-${this.sectionId} variant-selects, #MainProduct-${this.sectionId} variant-radios`);
        if (mainVariantSelect) {
          const inputToChange = mainVariantSelect.querySelector(`input[value="${variantId}"], select option[value="${variantId}"]`);
          if (inputToChange) {
            if (inputToChange.tagName === 'OPTION') {
              inputToChange.parentElement.value = variantId;
              inputToChange.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              inputToChange.checked = true;
              inputToChange.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }

        const mainIdInput = document.querySelector(`#MainProduct-${this.sectionId} input[name="id"], form[action*="/cart/add"] input[name="id"]`);
        if (mainIdInput) {
          mainIdInput.value = variantId;
        }
      }

      updateDisplayForVariant(option) {
        if (!option) return;

        const price = option.dataset.price;
        const isAvailable = option.dataset.available === 'true';
        const imgUrl = option.dataset.image;

        if (price && this.priceEl) {
          this.priceEl.textContent = price;
        }

        if (imgUrl && this.imageEl) {
          this.imageEl.src = imgUrl;
        }

        if (this.submitBtn) {
          const submitText = this.submitBtn.querySelector('.sticky-atc__submit-text');
          if (isAvailable) {
            this.submitBtn.disabled = false;
            this.submitBtn.classList.remove('is-disabled');
            if (submitText) submitText.textContent = window.variantStrings?.addToCart || 'Add to Cart';
          } else {
            this.submitBtn.disabled = true;
            this.submitBtn.classList.add('is-disabled');
            if (submitText) submitText.textContent = window.variantStrings?.soldOut || 'Sold Out';
          }
        }
      }

      getCurrentVariantId() {
        if (this.variantSelect && this.variantSelect.value) {
          return this.variantSelect.value;
        }
        const hiddenInput = this.querySelector('.sticky-atc__variant-id');
        if (hiddenInput && hiddenInput.value) {
          return hiddenInput.value;
        }
        const mainIdInput = document.querySelector(`#MainProduct-${this.sectionId} input[name="id"], form[action*="/cart/add"] input[name="id"]`);
        if (mainIdInput && mainIdInput.value) {
          return mainIdInput.value;
        }
        return null;
      }

      addToCart() {
        const variantId = this.getCurrentVariantId();
        if (!variantId || this.submitBtn.disabled) return;

        const quantity = this.qtyInput ? parseInt(this.qtyInput.value, 10) || 1 : 1;

        // Set loading state
        this.submitBtn.classList.add('is-loading');
        this.submitBtn.disabled = true;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(this.submitBtn);
        }

        const requestBody = {
          items: [
            {
              id: parseInt(variantId, 10),
              quantity: parseInt(quantity, 10)
            }
          ]
        };

        const sections = [];
        if (cart && typeof cart.getSectionsToRender === 'function') {
          cart.getSectionsToRender().forEach((sec) => sections.push(sec.id));
        } else {
          sections.push('cart-icon-bubble');
          sections.push('cart-drawer');
        }

        if (sections.length > 0) {
          requestBody.sections = sections;
          requestBody.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })
          .then((response) => response.json())
          .then((data) => {
            console.log('Added to cart:', data);

            if (data.status) {
              if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'sticky-add-to-cart',
                  productVariantId: variantId,
                  errors: data.errors || data.description,
                  message: data.message
                });
              }
              alert(data.description || data.message || 'Error adding item to cart');
              return;
            }

            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-add-to-cart',
                productVariantId: variantId,
                cartData: data
              });
            }

            if (cart && typeof cart.renderContents === 'function') {
              if (cart.classList.contains('is-empty')) {
                cart.classList.remove('is-empty');
              }
              cart.renderContents(data);
            }

            // Update cart-icon-bubble in header if available in sections response
            if (data.sections && data.sections['cart-icon-bubble']) {
              const iconBubble = document.getElementById('cart-icon-bubble');
              if (iconBubble) {
                const parsedBubble = new DOMParser()
                  .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
                  .getElementById('cart-icon-bubble');
                if (parsedBubble) {
                  iconBubble.innerHTML = parsedBubble.innerHTML;
                }
              }
            }

            // Visual feedback on button
            const submitText = this.submitBtn.querySelector('.sticky-atc__submit-text');
            const originalText = submitText ? submitText.textContent : 'Add to Cart';
            if (submitText) {
              submitText.textContent = 'Added! ✓';
              setTimeout(() => {
                submitText.textContent = originalText;
              }, 2000);
            }
          })
          .catch((err) => {
            console.error('Add to cart failed:', err);
          })
          .finally(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.disabled = false;
          });
      }
    }
  );
}
